// utils/img.js

// Webpack-only: map relative asset keys (still useful if you keep string paths in data)
let MAP = {};
try {
  const ctx = require.context('../assets', true, /\.(png|jpe?g|webp|gif|svg)$/i);
  ctx.keys().forEach((k) => {
    const url = ctx(k); // emitted URL (often "/assets/hash.ext")
    const clean = k.replace(/^.\//, ''); // e.g. "folder/file.jpg"
    MAP[clean] = url;
    MAP['/' + clean] = url;
  });
} catch (e) {
  // Vite or non-Webpack builds won't have require.context — that's OK.
  MAP = {};
}

// Get deployment base (subpath) safely
function getBase() {
  // CRA
  if (typeof process !== 'undefined' && process.env && process.env.PUBLIC_URL) {
    return process.env.PUBLIC_URL.replace(/\/+$/, '');
  }
  // Vite
  if (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.BASE_URL) {
    return String(import.meta.env.BASE_URL).replace(/\/+$/, '');
  }
  return ''; // site at domain root
}

const BASE = getBase();

/**
 * Always return a URL that works from nested routes.
 * Accepts:
 *  - imported URLs ("/assets/hash.png")
 *  - absolute URLs ("https://...")
 *  - relative keys that exist in MAP ("folder/file.jpg")
 *  - bare relative strings ("images/x.jpg")
 */
export function imgSrc(src) {
  if (!src) return '';

  // If it's already an absolute HTTP(S) or data/blob URL, return as is.
  if (/^(https?:)?\/\//i.test(src) || /^data:|^blob:/i.test(src)) return src;

  // If we have a mapped asset (Webpack ctx)
  if (MAP[src]) return withBase(MAP[src]);

  // Try some forgiving variants for string keys
  const variants = [
    String(src).replace(/^\.?\//, ''),   // "./x.jpg" -> "x.jpg"
    String(src).replace(/^assets\//i, ''), // "assets/x.jpg" -> "x.jpg"
    String(src).replace(/^src\//i, ''),    // "src/assets/x.jpg" -> "assets/x.jpg"
    String(src).replace(/^images\//i, ''), // "images/x.jpg" -> "x.jpg"
  ];
  for (const v of variants) {
    if (MAP[v]) return withBase(MAP[v]);
  }

  // If it's a root-relative emitted path like "/assets/hash.png",
  // prefix the deployment base if present.
  if (String(src).startsWith('/')) return withBase(src);

  // Otherwise treat it as public/ file at the base
  return withBase('/' + src);
}

// Prefix emitted root-relative paths with BASE when needed
function withBase(url) {
  if (!BASE) return url;         // site at "/"
  if (/^https?:\/\//i.test(url)) return url; // external
  // Avoid double-prefixing if url already includes BASE
  if (url.startsWith(BASE + '/')) return url;
  if (url.startsWith('/')) return BASE + url;
  return BASE + '/' + url.replace(/^\/+/, '');
}
